import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaHome, FaSearch, FaChevronLeft, FaChevronRight, FaFilter } from 'react-icons/fa';
import DashboardLayout from '../layout/DashboardLayout';
import DashboardHeader from '../dashboard/DashboardHeader';
import Spinner from '../common/Spinner';
import { fetchProjects } from '../../store/slices/projectsSlice';
import { config } from '../../config/config';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import styles from '../leads/MapView.module.css';
import { format } from 'date-fns';

// Set mapbox token
mapboxgl.accessToken = config.mapboxAccessToken;

// ProjectCard component for the sidebar
const ProjectCard = ({ project, isSelected, onClick, onViewInMap, error }) => {
  const dispatch = useDispatch();
  const { projects, loading: apiLoading } = useSelector((state) => state.projects);
  const [geocodingErrors, setGeocodingErrors] = useState({});
  const [geocodingLoading, setGeocodingLoading] = useState(false);

  const getGoogleMapsUrl = (address) => {
    if (!address) return null;
    const query = encodeURIComponent(
      `${address.street}, ${address.city}, ${address.state} ${address.zipCode || ''}`
    );
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  };

  // Use site contact address if available, fallback to lead address
  const displayAddress = project.siteContact?.address || project.address;
  const hasValidCoordinates = project.coordinates && Array.isArray(project.coordinates) && project.coordinates.length === 2;

  // Function to get status color and text
  const getStatusInfo = (status) => {
    switch (status) {
      case 'active':
        return { color: 'bg-green-100 text-green-700', text: 'Active' };
      case 'on_hold':
        return { color: 'bg-yellow-100 text-yellow-700', text: 'On Hold' };
      case 'completed':
        return { color: 'bg-blue-100 text-blue-700', text: 'Completed' };
      case 'cancelled':
        return { color: 'bg-red-100 text-red-700', text: 'Cancelled' };
      case 'archived':
        return { color: 'bg-gray-100 text-gray-700', text: 'Archived' };
      default:
        return { color: 'bg-gray-100 text-gray-700', text: status };
    }
  };

  const statusInfo = getStatusInfo(project.projectStatus);

  return (
    <div 
      className={`
        bg-white rounded-lg p-3 md:p-4 cursor-pointer
        transition-all duration-200 ease-in-out
        ${isSelected ? 
          'border-l-4 border-blue-500 bg-blue-50 shadow-md' : 
          'border-l-4 border-transparent hover:border-blue-300 hover:bg-gray-50 shadow-sm'
        }
        ${error ? 'border-l-4 !border-red-400' : ''}
      `}
      onClick={onClick}
    >
      <div className="flex justify-between items-start mb-2 md:mb-3">
        <div className="flex items-center space-x-1 md:space-x-2">
          <FaMapMarkerAlt 
            className={`w-4 h-4 md:w-5 md:h-5 transition-colors duration-200 ${
              isSelected ? 'text-blue-500' : 'text-gray-400'
            }`} 
          />
          <span className={`text-sm md:text-base font-medium transition-colors duration-200 ${
            isSelected ? 'text-blue-700' : 'text-gray-700'
          }`}>
            {project.jobNumber}
          </span>
        </div>
        <div className="flex flex-col items-end space-y-1">
          <span className={`
            px-2 py-1 text-xs font-medium rounded-full
            ${project.priority === 'high' ? 'bg-red-100 text-red-700' : 
              project.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : 
              'bg-green-100 text-green-700'}
          `}>
            {project.priority}
          </span>
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusInfo.color}`}>
            {statusInfo.text}
          </span>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <h3 className={`text-lg font-semibold transition-colors duration-200 ${
            isSelected ? 'text-blue-900' : 'text-gray-800'
          }`}>
            {project.siteContact?.name || project.client?.name}
          </h3>
          <p className="text-sm text-gray-500">
            {project.opportunityType || 'Project'}
          </p>
        </div>

        <div className="space-y-1">
          <p className={`text-sm ${isSelected ? 'text-blue-800' : 'text-gray-600'}`}>
            {displayAddress?.street}
          </p>
          <p className={`text-sm ${isSelected ? 'text-blue-700' : 'text-gray-600'}`}>
            {displayAddress?.city}, {displayAddress?.state} {displayAddress?.zipCode}
          </p>
          {displayAddress && (
            <div className="flex space-x-3">
              <a
                href={getGoogleMapsUrl(displayAddress)}
                target="_blank"
                rel="noopener noreferrer"
                className={`
                  inline-flex items-center space-x-1 text-xs mt-1
                  ${isSelected ? 'text-blue-600 hover:text-blue-700' : 'text-gray-500 hover:text-gray-700'}
                `}
                onClick={(e) => e.stopPropagation()}
              >
                <FaMapMarkerAlt className="w-3 h-3" />
                <span>View in Maps</span>
              </a>
              <div className="relative group">
                <button
                  className={`
                    inline-flex items-center space-x-1 text-xs mt-1
                    ${hasValidCoordinates 
                      ? (isSelected ? 'text-blue-600 hover:text-blue-700' : 'text-gray-500 hover:text-gray-700')
                      : 'text-gray-400 cursor-not-allowed'
                    }
                  `}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (hasValidCoordinates) {
                      onViewInMap();
                    }
                  }}
                  disabled={!hasValidCoordinates}
                >
                  <FaMapMarkerAlt className="w-3 h-3" />
                  <span>View</span>
                </button>
                {!hasValidCoordinates && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                    No map location available
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-4 text-sm text-gray-500">
          {project.siteContact?.cellPhone && (
            <div className="flex items-center space-x-1">
              <FaPhone className={isSelected ? 'text-blue-400' : 'text-gray-400'} />
              <span>{project.siteContact.cellPhone}</span>
            </div>
          )}
          {project.siteContact?.email && (
            <div className="flex items-center space-x-1">
              <FaEnvelope className={isSelected ? 'text-blue-400' : 'text-gray-400'} />
              <span className="truncate max-w-[150px]">{project.siteContact.email}</span>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center text-xs pt-2 border-t border-gray-100">
          <div className="flex items-center space-x-1">
            <FaClock className={isSelected ? 'text-blue-400' : 'text-gray-400'} />
            <span className={isSelected ? 'text-blue-600' : 'text-gray-400'}>
              {format(new Date(project.createdAt), 'MMM d, yyyy')}
            </span>
          </div>
          {error && (
            <span className="text-red-500 font-medium">
              {error}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Rename MapComponent to ProjectMapComponent
const ProjectMapComponent = ({ mapProjects, handleProjectClick, selectedProject }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef({});
  const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/streets-v11');

  // Available map styles
  const mapStyles = [
    { id: 'mapbox://styles/mapbox/streets-v11', name: 'Streets' },
    { id: 'mapbox://styles/mapbox/satellite-streets-v11', name: 'Satellite' },
    { id: 'mapbox://styles/mapbox/light-v10', name: 'Light' },
    { id: 'mapbox://styles/mapbox/dark-v10', name: 'Dark' },
    { id: 'mapbox://styles/mapbox/outdoors-v11', name: 'Outdoors' },
    { id: 'mapbox://styles/mapbox/navigation-day-v1', name: 'Navigation' }
  ];

  // Create custom home icon SVG
  const createHomeIcon = (color) => {
    return `data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${color}"><path d="M12 2.1L1 12h3v9h7v-6h2v6h7v-9h3L12 2.1zm0 2.691l6 5.4V19h-3v-6H9v6H6v-8.809l6-5.4z"/></svg>`;
  };

  // Handle style change
  const handleStyleChange = (styleId) => {
    setMapStyle(styleId);
    if (map.current) {
      map.current.setStyle(styleId);
    }
  };

  useEffect(() => {
    if (!map.current) {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: mapStyle,
        center: [-98.5795, 39.8283], // Default to US center
        zoom: 3.5, // Lower initial zoom level
        pitchWithRotate: true,
        dragRotate: true
      });

      // Add controls
      map.current.addControl(new mapboxgl.NavigationControl());
      map.current.addControl(new mapboxgl.FullscreenControl());
      map.current.addControl(new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        trackUserLocation: true,
        showUserHeading: true
      }));
      
      map.current.addControl(new mapboxgl.ScaleControl({
        maxWidth: 80,
        unit: 'imperial'
      }));

      map.current.on('style.load', () => {
        map.current.addSource('mapbox-dem', {
          'type': 'raster-dem',
          'url': 'mapbox://mapbox.mapbox-terrain-dem-v1',
          'tileSize': 512,
          'maxzoom': 14
        });
        map.current.setTerrain({ 'source': 'mapbox-dem', 'exaggeration': 1.5 });
      });
    }

    return () => {
      Object.values(markersRef.current).forEach(marker => marker.remove());
      markersRef.current = {};
      
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [mapStyle]);

  // Update popup content to match leads style
  const createPopupContent = (project) => `
    <div class="p-4">
      <div class="flex items-start justify-between mb-3">
        <div>
          <h3 class="font-medium text-lg text-gray-900">${project.jobNumber}</h3>
          <p class="text-sm text-gray-500">${format(new Date(project.createdAt), 'MMM d, yyyy')}</p>
        </div>
        <span class="px-2 py-1 text-sm rounded-full ${
          project.projectStatus === 'active' ? 'bg-green-100 text-green-800' :
          project.projectStatus === 'completed' ? 'bg-blue-100 text-blue-800' :
          'bg-gray-100 text-gray-800'
        }">
          ${project.projectStatus}
        </span>
      </div>

      <div class="space-y-2">
        <p class="text-gray-600">${project.formattedAddress || 
          `${project.address?.street}, ${project.address?.city}, ${project.address?.state} ${project.address?.zipCode || ''}`}</p>
        
        ${project.client?.name ? 
          `<p class="text-gray-600 font-medium">${project.client.name}</p>` : ''}
        
        ${project.client?.cellPhone ? 
          `<p class="text-gray-600"><i class="fas fa-phone mr-2"></i>${project.client.cellPhone}</p>` : ''}
        
        ${project.geocodingConfidence < 0.8 ? 
          `<p class="text-amber-600 text-xs mt-2">⚠️ Address match confidence: ${Math.round(project.geocodingConfidence * 100)}%</p>` 
          : ''}
      </div>
    </div>
  `;

  // Handle markers
  useEffect(() => {
    if (!map.current || !mapProjects.length) return;

    // Need to wait for style to load before adding markers
    const onStyleLoaded = () => {
      Object.values(markersRef.current).forEach(marker => marker.remove());
      markersRef.current = {};

      // Create bounds object to fit all markers
      const bounds = new mapboxgl.LngLatBounds();

      mapProjects.forEach(project => {
        const el = document.createElement('div');
        el.className = 'custom-marker';
        
        el.style.width = '45px';
        el.style.height = '45px';
        el.style.backgroundImage = `url('${createHomeIcon(
          project.projectStatus === 'active' ? '%2322c55e' : 
          project.projectStatus === 'completed' ? '%231d4ed8' : 
          '%23737373'
        )}')`;
        el.style.backgroundSize = 'contain';
        el.style.backgroundRepeat = 'no-repeat';
        el.style.cursor = 'pointer';

        const popup = new mapboxgl.Popup({ 
          offset: [0, -20],
          closeButton: false,
          maxWidth: '300px'
        })
        .setHTML(createPopupContent(project));

        const marker = new mapboxgl.Marker({
          element: el,
          anchor: 'bottom'
        })
        .setLngLat([project.coordinates[1], project.coordinates[0]])
        .setPopup(popup)
        .addTo(map.current);

        // Extend bounds with this point
        bounds.extend([project.coordinates[1], project.coordinates[0]]);

        el.addEventListener('mouseenter', () => {
          marker.getPopup().addTo(map.current);
        });

        el.addEventListener('mouseleave', () => {
          if (selectedProject?._id !== project._id) {
            marker.getPopup().remove();
          }
        });

        el.addEventListener('click', () => {
          handleProjectClick(project);
        });

        markersRef.current[project._id] = marker;
      });

      // Fit map to bounds with padding
      if (!bounds.isEmpty()) {
        map.current.fitBounds(bounds, {
          padding: 50,
          maxZoom: 15
        });
      }
    };

    // If style is loaded, add markers immediately; otherwise, wait for the style.load event
    if (map.current.isStyleLoaded()) {
      onStyleLoaded();
    } else {
      map.current.once('style.load', onStyleLoaded);
    }
  }, [mapProjects, handleProjectClick, selectedProject, mapStyle]);

  // Handle selected project
  useEffect(() => {
    if (!map.current || !selectedProject) return;

    map.current.flyTo({
      center: [selectedProject.coordinates[1], selectedProject.coordinates[0]],
      zoom: 16,
      pitch: 60,
      bearing: 30,
      duration: 2000,
      essential: true
    });

    Object.entries(markersRef.current).forEach(([id, marker]) => {
      const el = marker.getElement();
      if (id === selectedProject._id) {
        el.style.transform = 'scale(1.2) translateY(-10px)';
        el.style.zIndex = 1;
        marker.getPopup().addTo(map.current);
      } else {
        el.style.transform = 'scale(1) translateY(0)';
        el.style.zIndex = 0;
        marker.getPopup().remove();
      }
    });
  }, [selectedProject]);

  return (
    <div className="relative h-full">
      <div className="absolute top-3 right-3 z-10 bg-white rounded-md shadow-md p-2">
        <div className="text-xs font-medium mb-1 text-gray-500">Map Style</div>
        <div className="grid grid-cols-1 gap-1">
          {mapStyles.map(style => (
            <button 
              key={style.id}
              onClick={() => handleStyleChange(style.id)}
              className={`text-xs py-1 px-2 rounded ${
                mapStyle === style.id 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {style.name}
            </button>
          ))}
        </div>
      </div>
      <div ref={mapContainer} className={styles.mapContainer} />
    </div>
  );
};

const ProjectMapView = () => {
  const dispatch = useDispatch();
  const { projects, loading: apiLoading } = useSelector((state) => state.projects);
  const [searchTerm, setSearchTerm] = useState('');
  const [mapProjects, setMapProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [geocodingErrors, setGeocodingErrors] = useState({});
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [projectStatus, setProjectStatus] = useState('all');

  // Load projects once without search params - get a larger batch
  const loadProjects = useCallback(async () => {
    try {
      const params = {
        page: 1,
        limit: 100, // Get a larger batch of projects for client-side filtering
        projectStatus: '' // Get all statuses
      };

      await dispatch(fetchProjects(params)).unwrap();
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  }, [dispatch]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Client-side filtering with useCallback
  const handleClientSearch = useCallback((term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page on new search
  }, []);

  // Client-side filtering for projects
  useMemo(() => {
    if (!mapProjects.length) return;

    let filtered = [...mapProjects];

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(project => {
        const nameMatch = (project.client?.name || '').toLowerCase().includes(searchLower) ||
                         (project.siteContact?.name || '').toLowerCase().includes(searchLower);
        const addressMatch = (project.formattedAddress || '').toLowerCase().includes(searchLower) ||
                           (project.siteContact?.address?.street || '').toLowerCase().includes(searchLower) ||
                           (project.siteContact?.address?.city || '').toLowerCase().includes(searchLower) ||
                           (project.address?.street || '').toLowerCase().includes(searchLower) ||
                           (project.address?.city || '').toLowerCase().includes(searchLower);
        const jobNumberMatch = (project.jobNumber || '').toLowerCase().includes(searchLower);
        return nameMatch || addressMatch || jobNumberMatch;
      });
    }

    // Filter by status
    if (projectStatus !== 'all') {
      filtered = filtered.filter(project => project.projectStatus === projectStatus);
    }

    setFilteredProjects(filtered);
  }, [mapProjects, searchTerm, projectStatus]);

  // Pagination for filtered projects
  const paginatedProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * limit;
    const endIndex = startIndex + limit;
    return filteredProjects.slice(startIndex, endIndex);
  }, [filteredProjects, currentPage, limit]);

  // Calculate total pages
  const totalPages = useMemo(() => {
    return Math.ceil(filteredProjects.length / limit);
  }, [filteredProjects, limit]);

  // Pagination handlers
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  // Handle limit change
  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing limit
  };

  // Handle status change - client-side only
  const handleStatusChange = (e) => {
    setProjectStatus(e.target.value);
    setCurrentPage(1); // Reset to first page when changing status
  };

  // Update the formatAddress function to prioritize site contact address
  const formatAddress = (project) => {
    // Prioritize site contact address, then project address, then client address
    const address = project.siteContact?.address || project.address || project.client?.address;
    
    if (!address) return null;

    const formatString = (str) => str?.trim().replace(/\s+/g, ' ').replace(/[^\w\s,-]/g, '');

    const street = formatString(address.street);
    const city = formatString(address.city);
    const state = formatString(address.state);
    const zipCode = address.zipCode?.trim();

    if (!street || !city || !state) {
      return null;
    }

    let formattedAddress = `${street}, ${city}, ${state}`;
    if (zipCode) {
      formattedAddress += ` ${zipCode}`;
    }

    return formattedAddress;
  };

  // Update the geocoding function to set both mapProjects and filteredProjects
  useEffect(() => {
    const getCoordinates = async () => {
      setGeocodingLoading(true);
      const newGeocodingErrors = {};
      
      try {
        const projectsWithCoordinates = await Promise.all(
          projects.map(async (project) => {
            try {
              const formattedAddress = formatAddress(project);
              
              if (!formattedAddress) {
                newGeocodingErrors[project._id] = 'Invalid or incomplete address';
                return null;
              }

              const searchAddress = `${formattedAddress}, USA`;
              
              const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchAddress)}.json?` + 
                new URLSearchParams({
                  access_token: config.mapboxAccessToken,
                  limit: 1,
                  types: 'address',
                  country: 'US',
                  autocomplete: false
                })
              );
              
              if (!response.ok) {
                throw new Error('Geocoding request failed');
              }

              const data = await response.json();
              
              if (data?.features?.[0]) {
                const [lon, lat] = data.features[0].center;
                const confidence = data.features[0].relevance;

                if (confidence < 0.8) {
                  newGeocodingErrors[project._id] = 'Low confidence address match';
                }

                return {
                  ...project,
                  coordinates: [lat, lon],
                  formattedAddress: data.features[0].place_name,
                  geocodingConfidence: confidence
                };
              }
              
              newGeocodingErrors[project._id] = 'Could not find coordinates for this address';
              return null;

            } catch (error) {
              console.error('Geocoding error:', error);
              newGeocodingErrors[project._id] = 'Error geocoding address';
              return null;
            }
          })
        );

        setGeocodingErrors(newGeocodingErrors);
        const validProjects = projectsWithCoordinates.filter(project => project !== null);
        setMapProjects(validProjects);
        setFilteredProjects(validProjects); // Initialize filtered projects with all valid projects
      } catch (error) {
        console.error('Geocoding process error:', error);
      } finally {
        setGeocodingLoading(false);
      }
    };

    if (projects && projects.length > 0) {
      getCoordinates();
    } else {
      setMapProjects([]);
      setFilteredProjects([]);
    }
  }, [projects]);

  const handleProjectClick = (project) => {
    setSelectedProject(project);
  };

  const handleViewInMap = (project) => {
    setSelectedProject(project);
  };

  return (
    <DashboardLayout>
      <DashboardHeader 
        hideCreateLead={true}
        hideActions={true}
        hideTabBar={true}
        hideSearch={true}
      />
      
      <div className="p-2 md:p-4 h-[calc(100vh-120px)] flex flex-col">
        {apiLoading || geocodingLoading ? (
          <div className="flex flex-col items-center justify-center h-full">
            <Spinner size="xl" color="blue" />
            <p className="mt-4 text-gray-600">
              {apiLoading ? 'Loading projects...' : 'Processing addresses...'}
            </p>
          </div>
        ) : (
          <div className="flex flex-col md:flex-row gap-2 md:gap-4 h-full">
            {/* Map */}
            <div className="h-[40vh] md:h-full md:flex-1 order-2 md:order-1">
              <div className="bg-white rounded-lg shadow-lg h-full">
                <ProjectMapComponent 
                  mapProjects={mapProjects} // Show all projects on map
                  handleProjectClick={handleProjectClick}
                  selectedProject={selectedProject}
                />
              </div>
            </div>

            {/* Project Cards */}
            <div className="w-full md:w-[420px] order-1 md:order-2 flex flex-col h-[60vh] md:h-full">
              {/* Search and Filter Controls */}
              <div className="bg-white rounded-lg shadow-sm mb-2 md:mb-4 p-3 md:p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-base md:text-lg font-medium text-gray-900">Projects</h2>
                  <p className="text-xs md:text-sm text-gray-500">
                    {filteredProjects.length} projects found
                  </p>
                </div>

                {/* Search bar */}
                <div className="relative mb-3">
                  <input
                    type="text"
                    placeholder="Search projects..."
                    value={searchTerm}
                    onChange={(e) => handleClientSearch(e.target.value)}
                    className="w-full p-2 pl-10 pr-4 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <FaSearch className="absolute left-3 top-3 text-gray-400" />
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-2">
                  <div className="flex items-center flex-1">
                    <FaFilter className="text-gray-400 mr-2" />
                    <select 
                      value={projectStatus} 
                      onChange={handleStatusChange}
                      className="text-sm border rounded p-1 bg-white flex-1"
                    >
                      <option value="all">All Statuses</option>
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center">
                    <label className="text-sm text-gray-500 mr-2">Per page:</label>
                    <select 
                      value={limit} 
                      onChange={handleLimitChange}
                      className="text-sm border rounded p-1 bg-white"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Scrollable area for project cards */}
              <div className="flex-1 overflow-y-auto mb-2 custom-scrollbar">
                <div className="space-y-2 md:space-y-3 pr-2">
                  {filteredProjects.length === 0 ? (
                    <div className="text-center p-4 md:p-8 bg-white rounded-lg shadow-sm">
                      <div className="text-gray-400 mb-3">
                        <FaMapMarkerAlt className="w-8 md:w-12 h-8 md:h-12 mx-auto" />
                      </div>
                      <p className="text-gray-500 text-base md:text-lg">
                        No projects found matching filters
                      </p>
                    </div>
                  ) : (
                    paginatedProjects.map((project) => (
                      <ProjectCard
                        key={project._id}
                        project={project}
                        isSelected={selectedProject?._id === project._id}
                        onClick={() => handleProjectClick(project)}
                        onViewInMap={() => handleViewInMap(project)}
                        error={geocodingErrors[project._id]}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Pagination controls */}
              {totalPages > 1 && (
                <div className="mt-auto flex items-center justify-between bg-white p-3 rounded-lg shadow-sm">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className={`flex items-center space-x-1 text-sm 
                      ${currentPage === 1 
                        ? 'text-gray-300 cursor-not-allowed' 
                        : 'text-blue-600 hover:text-blue-800'}`}
                  >
                    <FaChevronLeft className="w-3 h-3" />
                    <span>Previous</span>
                  </button>
                  
                  <div className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </div>
                  
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage >= totalPages}
                    className={`flex items-center space-x-1 text-sm 
                      ${currentPage >= totalPages 
                        ? 'text-gray-300 cursor-not-allowed' 
                        : 'text-blue-600 hover:text-blue-800'}`}
                  >
                    <span>Next</span>
                    <FaChevronRight className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ProjectMapView; 